import { useContext, useEffect, useRef } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';
import { useApiEnvironment } from '../context/ApiEnvironmentContext';

// BrowserRouter uses the native history index. Keep declined pop navigation on
// the current entry before React Router receives the event.
export function useUnsavedChanges(dirty, busy = false, prompt = '저장하지 않은 초안 변경이 있습니다. 변경을 버리고 이동할까요?') {
  const navigation = useContext(UNSAFE_NavigationContext);
  const { registerEnvironmentGuard } = useApiEnvironment();
  const state = useRef({ dirty, busy, prompt });
  state.current = { dirty, busy, prompt };
  useEffect(() => {
    const allow = () => !state.current.busy && (!state.current.dirty || window.confirm(state.current.prompt));
    const unregister = registerEnvironmentGuard?.(allow);
    const beforeUnload = event => { if (state.current.dirty || state.current.busy) { event.preventDefault(); event.returnValue = ''; } };
    window.addEventListener('beforeunload', beforeUnload);
    let index = window.history.state?.idx;
    let restoring = false;
    let approvedPop = false;
    const pop = event => {
      const next = event.state?.idx;
      if (restoring) { restoring = false; event.stopImmediatePropagation(); return; }
      if (approvedPop) { approvedPop = false; index = next; return; }
      if (allow()) { index = next; return; }
      if (Number.isInteger(index) && Number.isInteger(next) && index !== next) {
        event.stopImmediatePropagation();
        restoring = true;
        window.history.go(index - next);
      }
    };
    window.addEventListener('popstate', pop, true);
    const navigator = navigation?.navigator;
    const previous = {};
    ['push', 'replace', 'go'].forEach(method => {
      if (!navigator?.[method]) return;
      previous[method] = navigator[method];
      navigator[method] = (...args) => {
        const destination = args[0];
        const samePageSelection = method === 'replace' && typeof destination === 'object' && destination?.pathname === window.location.pathname;
        if (!samePageSelection && !allow()) return undefined;
        if (method === 'go') approvedPop = true;
        const result = previous[method].apply(navigator, args);
        if (method !== 'go') index = window.history.state?.idx;
        return result;
      };
    });
    return () => {
      unregister?.(); window.removeEventListener('beforeunload', beforeUnload); window.removeEventListener('popstate', pop, true);
      ['push', 'replace', 'go'].forEach(method => { if (navigator && previous[method]) navigator[method] = previous[method]; });
    };
  }, [navigation, registerEnvironmentGuard]);
}
